import { useEffect } from 'react'

import { toast } from 'react-toastify'

import { useForm, useSetState, useThunkDispatch } from '@domain-group/react-hooks'
import { digObject } from '@domain-group/javascript-utils'

import * as campaignActions from '@redux/modules/campaign'

import useAgencySelector from '@hooks/useAgencySelector'
import { generateUrls } from '@hooks/useCampaign'

import type { AppDispatch } from '@redux/store'
import type { AgencyModel, CampaignModel } from '@models/types'
import type { EntitiesState } from '@redux/entities'

export type CampaignSearchResult = {
  address: string,
  id: number,
  life_id: number,
  service_provider: string,
  status?: string,
}

const defaultState = {
  hasSearched: false,
  loading: false,
  results: [],
}

const initFormState = {
  agency_id: undefined,
  sale_method: 'sale',
  string: '',
}

export type InitFormState = typeof initFormState

type SetState = (state: Partial<typeof defaultState>) => void

const checkSaleMethodRequired = (agency: AgencyModel) => {
  const agency_crm_sync = digObject(agency, 'options.crm_sync_api', '')

  if (agency_crm_sync === 'vaultre'){
    return true
  }

  return false
}

export type ImportCampaignOptions = {
  agency_id: number,
  sale_method?: string,
}

type CampaignParams = {
  agency_id: number,
  id: number,
  sale_method: string,
}

type ImportCampaignParams = {
  agencies: EntitiesState['agencies'],
  dispatch: AppDispatch,
  entityParams: CampaignParams,
}

const importCampaign = (params: ImportCampaignParams) => {
  const { agencies, entityParams, dispatch } = params || {}
  const { agency_id, id, sale_method } = entityParams || {}

  const { importCampaign: importFn } = campaignActions

  const agency = agencies[agency_id] || {}

  const includeSaleMethod = checkSaleMethodRequired(agency)

  const options: ImportCampaignOptions = { agency_id }

  if (includeSaleMethod){
    options.sale_method = sale_method
  }

  return dispatch(importFn(id, options)).then((response) => {
    const { success } = response

    if (!success){
      const { errors } = response

      toast.warn(errors[0])
    }

    if (success){
      const { data } = response

      const { campaignEditDetailsUrl } = generateUrls(data)

      return {
        success,
        data,
        redirectUrl: campaignEditDetailsUrl,
      }
    }

    return response
  })
}

type CampaignSearchParams = {
  dispatch: AppDispatch,
  entityState: typeof initFormState,
  setState: SetState,
}

const campaignSearch = (params: CampaignSearchParams) => {
  const { dispatch, entityState, setState } = params
  const { agency_id, sale_method, string } = entityState

  const { campaignSearch: searchFn } = campaignActions

  setState({
    hasSearched: false,
    loading: true,
    results: [],
  })

  const options = {
    agency_id, sale_method, string,
  }

  // @ts-expect-error TS2345
  return dispatch(searchFn(options)).then((response) => {
    const { success } = response

    if (!success){
      setState({ loading: false })

      toast.warn('Could not search campaigns')
    }

    // @ts-expect-error TS2339
    const { data } = response

    return setState({
      hasSearched: true,
      loading: false,
      results: data.data,
    })
  })
}

const useImportCampaign = (initCampaign: Partial<CampaignModel>) => {
  const [state, setState] = useSetState(defaultState)
  const { hasSearched, results } = state

  const importCampaignForm = useForm(initFormState, {
    entity: initCampaign,
    validateOn: 'change',
    requiredFields: [
      { key: 'agency_id' },
      { key: 'string' },
    ],
  })
  const {
    entityState,
    entityState: {
      agency_id,
      sale_method,
    },
    handlers,
    saveEnabled,
    setEntityState,
  } = importCampaignForm

  const dispatch = useThunkDispatch()

  const agencySelectorPayload = useAgencySelector()
  const { agencies, sortedAgencies } = agencySelectorPayload

  const agency = agencies[agency_id] || {}
  const saleMethodRequired = checkSaleMethodRequired(agency)

  const hasMultipleAgencies = sortedAgencies.length > 1
  const hasSearchResults = !!results?.length

  useEffect(() => {
    if (hasSearched){
      // reset state
      setState({
        hasSearched: false,
        loading: false,
        results: [],
      })
    }
  }, [agency_id, sale_method])

  return {
    callbacks: {
      campaignSearch: () => campaignSearch({ dispatch, entityState, setState }),
      importCampaign: (entityParams: CampaignParams) => importCampaign({
        agencies, dispatch, entityParams,
      }),
      setState,
      setEntityState,
    },
    entityState,
    handlers,
    hasMultipleAgencies,
    hasSearchResults,
    saleMethodRequired,
    saveEnabled,
    sortedAgencies,
    state,
  }
}

export type useImportCampaignPayload = ReturnType<typeof useImportCampaign>

export default useImportCampaign
